import { SUPABASE_URL, SUPABASE_ANON_KEY, supabase } from './supabaseClient'

/**
 * Acesso Único (AUTH central) — o mesmo AUTH usado pelo PRISMA.
 *
 * A identidade é do AUTH central (OAuth 2.1 + PKCE). A SOFERA nunca recebe
 * senha: ela troca o `code` central por um token, entrega esse token para a
 * Edge Function `sofera-auth-bridge` e recebe de volta uma sessão GoTrue real
 * da SOFERA. A partir daí RLS, refresh, logout e impersonação seguem iguais.
 *
 * Convivência: o login local continua funcionando. Este é apenas um caminho
 * adicional.
 */

const CONFIG_PATH = '/functions/v1/sofera-central-config'
const BRIDGE_PATH = '/functions/v1/sofera-auth-bridge'
const CALLBACK_PATH = '/auth/callback'
const PENDING_KEY = 'sofera:central-oauth-pending'
const MAX_PENDING_AGE_MS = 5 * 60 * 1000

let configPromise = null
let callbackPromise = null
let capturedCallback = null

function base64Url(bytes) {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomUrlSafe(size) {
  return base64Url(crypto.getRandomValues(new Uint8Array(size)))
}

/** Config pública do AUTH central (URL, chave publishable e client_id). */
export async function loadCentralConfig() {
  if (!configPromise) {
    configPromise = (async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}${CONFIG_PATH}`, {
          headers: { apikey: SUPABASE_ANON_KEY },
        })
        if (!res.ok) return null
        const data = await res.json().catch(() => null)
        if (!data?.enabled) return null
        const authUrl = String(data.auth_url || '').replace(/\/+$/, '')
        const clientId = String(data.client_id || '').trim()
        if (!authUrl || !clientId) return null
        return {
          authUrl,
          anonKey: String(data.anon_key || ''),
          clientId,
          allowedOrigins: Array.isArray(data.allowed_origins) ? data.allowed_origins : [],
        }
      } catch {
        return null
      }
    })()
  }
  return configPromise
}

function getCentralCallbackUrl(config, origin) {
  const clean = String(origin || '').replace(/\/+$/, '')
  if (!config?.allowedOrigins?.includes(clean)) return null
  return `${clean}${CALLBACK_PATH}`
}

/** Inicia o fluxo: monta o PKCE e envia o navegador para o AUTH central. */
export async function beginCentralLogin({ nextView = null } = {}) {
  const config = await loadCentralConfig()
  if (!config) throw new Error('central_indisponivel')

  const origin = String(window.location.origin || '')
  const redirectUri = getCentralCallbackUrl(config, origin)
  if (!redirectUri) throw new Error('origem_nao_autorizada')

  const state = randomUrlSafe(32)
  const codeVerifier = randomUrlSafe(32)
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier))
  const codeChallenge = base64Url(new Uint8Array(digest))

  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({
      state, codeVerifier, redirectUri, nextView, createdAt: Date.now(),
    }))
  } catch {}

  const url = new URL(`${config.authUrl}/auth/v1/oauth/authorize`)
  url.searchParams.set('client_id', config.clientId)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('scope', 'openid email profile offline_access')
  url.searchParams.set('state', state)
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')

  window.location.assign(url.toString())
}

/**
 * Lê o retorno do AUTH central e limpa a URL. O cliente Supabase da SOFERA
 * também observa `?code=`: removemos os parâmetros antes que ele tente
 * interpretá-los como um code GoTrue.
 */
function captureCentralCallback() {
  if (capturedCallback) return capturedCallback
  try {
    if (String(window.location.pathname || '') !== CALLBACK_PATH) return null
    const params = new URLSearchParams(window.location.search || '')
    const code = params.get('code') || ''
    const state = params.get('state') || ''
    const error = params.get('error_description') || params.get('error') || ''
    if (!code && !error) return null
    capturedCallback = { code, state, error }
    const url = new URL(window.location.href)
    for (const key of ['code', 'state', 'error', 'error_description', 'error_code']) {
      url.searchParams.delete(key)
    }
    window.history.replaceState(window.history.state, '', url.toString())
  } catch {
    capturedCallback = null
  }
  return capturedCallback
}

function consumePending(state) {
  let raw = null
  try { raw = window.sessionStorage.getItem(PENDING_KEY) } catch {}
  try { window.sessionStorage.removeItem(PENDING_KEY) } catch {}
  let pending = null
  try { pending = raw ? JSON.parse(raw) : null } catch {}
  if (!pending?.codeVerifier || !pending?.redirectUri || !state || pending.state !== state) {
    throw new Error('central_autorizacao_invalida')
  }
  if (!Number.isFinite(pending.createdAt) || Date.now() - pending.createdAt > MAX_PENDING_AGE_MS) {
    throw new Error('central_autorizacao_expirada')
  }
  return pending
}

async function exchangeCentralCode({ config, code, codeVerifier, redirectUri }) {
  const res = await fetch(`${config.authUrl}/auth/v1/oauth/token`, {
    method: 'POST',
    headers: { apikey: config.anonKey, 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      redirect_uri: redirectUri,
      code,
      code_verifier: codeVerifier,
    }),
  })
  const payload = await res.json().catch(() => null)
  if (!res.ok || !payload?.access_token) throw new Error('central_troca_falhou')
  return String(payload.access_token)
}

/** Troca o token central por uma sessão GoTrue real da SOFERA. */
async function bridgeCentralSession(centralAccessToken) {
  const res = await fetch(`${SUPABASE_URL}${BRIDGE_PATH}`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      'x-central-access-token': centralAccessToken,
    },
    body: JSON.stringify({}),
  })
  const payload = await res.json().catch(() => null)
  if (!res.ok) {
    const err = new Error(String(payload?.error || 'central_ponte_falhou'))
    err.code = String(payload?.error || 'central_ponte_falhou')
    throw err
  }
  if (!payload?.access_token || !payload?.refresh_token) throw new Error('central_sessao_invalida')
  return payload
}

async function runCentralCallback() {
  const callback = captureCentralCallback()
  if (!callback) throw new Error('central_retorno_ausente')
  if (callback.error) throw new Error('central_autorizacao_recusada')

  const config = await loadCentralConfig()
  if (!config) throw new Error('central_indisponivel')

  const pending = consumePending(callback.state)
  const centralAccessToken = await exchangeCentralCode({
    config,
    code: callback.code,
    codeVerifier: pending.codeVerifier,
    redirectUri: pending.redirectUri,
  })

  const session = await bridgeCentralSession(centralAccessToken)
  const { error } = await supabase.auth.setSession({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  })
  if (error) throw new Error('central_sessao_invalida')

  return { nextView: pending.nextView || null }
}

/** Conclui o retorno do AUTH central. Reentrante (StrictMode/duplo efeito). */
export function completeCentralLogin() {
  if (!callbackPromise) callbackPromise = runCentralCallback()
  return callbackPromise
}

export function describeCentralLoginError(err) {
  const code = String(err?.code || err?.message || '')
  switch (code) {
    case 'sem_acesso_sofera':
      return 'Sua conta do Acesso Único ainda não tem acesso à SOFERA. Solicite a liberação ao RH da sua empresa.'
    case 'usuario_inativo':
      return 'Sua conta na SOFERA está inativa. Fale com o suporte.'
    case 'perfil_sem_email':
      return 'Seu cadastro na SOFERA está sem e-mail. Fale com o suporte.'
    case 'origem_nao_autorizada':
      return 'Este endereço não está autorizado no Acesso Único.'
    case 'central_autorizacao_invalida':
    case 'central_autorizacao_expirada':
    case 'central_autorizacao_recusada':
      return 'A autorização expirou. Tente entrar novamente.'
    case 'central_indisponivel':
      return 'O Acesso Único está indisponível no momento. Entre com e-mail e senha.'
    default:
      return 'Não foi possível entrar com o Acesso Único. Tente novamente ou use e-mail e senha.'
  }
}
